import { ValueOf } from '@sato/components';

import {
  BaseIncidentFields,
  DefaultIncidentRequest,
  DefectIncidentRequest,
  DisturbanceIncidentRequest,
  OrderNewKeyRequest,
} from '@models/api';
import { Incident } from '@models/Incident';
import { UserMessageReply } from '@models/Message';
import { createAsyncTask } from '@stores/taskStore';

import { generateApiUrl } from './api.utils';
import { authClient } from './client.api';

export const INCIDENTS_API_URL = process.env.INCIDENTS_API_URL || '';

export const INCIDENTS_API_URLS = {
  FETCH_INCIDENT_MESSAGES: `${INCIDENTS_API_URL}/incident/posts/:incidentId`,
  FETCH_INCIDENTS: `${INCIDENTS_API_URL}/incidents/:contractId`,

  POST_INCIDENT_REPLY: `${INCIDENTS_API_URL}/incident/create/post`,
  INCIDENTS_CREATE: `${INCIDENTS_API_URL}/incident/create`,
  INCIDENTS_CREATE_ATTACHMENTS: `${INCIDENTS_API_URL}/incident/create/withattachments`,
  INCIDENTS_CREATE_DISTURBANCE: `${INCIDENTS_API_URL}/incident/create/disturbance`,
  INCIDENTS_CREATE_GENERIC_DEFECT: `${INCIDENTS_API_URL}/incident/create/defect`,
  INCIDENTS_CREATE_APARTMENT_DEFECT: `${INCIDENTS_API_URL}/incident/create/defect`,
  INCIDENTS_CREATE_ORDER_KEY: `${INCIDENTS_API_URL}/incident/create/orderkey`,
  INCIDENTS_CREATE_APARTMENT_MAINTENANCE: `${INCIDENTS_API_URL}/incident/create/apartment-maintenance`,
  INCIDENTS_CREATE_LOST_KEY: `${INCIDENTS_API_URL}/incident/create/lostkey`,
  INCIDENTS_CREATE_NAME_UPDATE: `${INCIDENTS_API_URL}/incident/create/name-update`,
} as const;

const createIncidenPostFormApi = <
  PayloadT extends BaseIncidentFields = DefaultIncidentRequest,
>(
  taskId: string,
  url: ValueOf<typeof INCIDENTS_API_URLS>
) =>
  createAsyncTask(taskId, (payload: PayloadT) =>
    authClient.postForm(url, payload)
  );

export const incidentApi = {
  fetchIncidentMessages: (incidentId: string) =>
    authClient.get<{ incidentPosts: UserMessageReply[] }>(
      generateApiUrl(INCIDENTS_API_URLS.FETCH_INCIDENT_MESSAGES, { incidentId })
    ),

  fetchIncidents: (contractId: string) =>
    authClient.get<{
      // TODO: is this a typo?
      contactIncidents: Incident[];
    }>(generateApiUrl(INCIDENTS_API_URLS.FETCH_INCIDENTS, { contractId })),

  postIncidentReply: (payload: {
    incidentId: string;
    text: string;
    // TODO: What are these?
    source: number;
    type: number;
  }) => authClient.post(INCIDENTS_API_URLS.POST_INCIDENT_REPLY, payload),

  postGenericIncident: createAsyncTask(
    'create-incident',
    (data: DefaultIncidentRequest) => {
      if (data.files?.length) {
        return authClient.postForm(
          INCIDENTS_API_URLS.INCIDENTS_CREATE_ATTACHMENTS,
          data
        );
      }

      return authClient.post(INCIDENTS_API_URLS.INCIDENTS_CREATE, data);
    }
  ),

  postDisturbanceIncident: createIncidenPostFormApi<DisturbanceIncidentRequest>(
    'create-disturbance-incident',
    INCIDENTS_API_URLS.INCIDENTS_CREATE_DISTURBANCE
  ),

  postApartmentDefectIncident: createIncidenPostFormApi<DefectIncidentRequest>(
    'post-defect-incident',
    INCIDENTS_API_URLS.INCIDENTS_CREATE_APARTMENT_DEFECT
  ),

  postGenericDefectIncident: createIncidenPostFormApi(
    'post-generic-defect-incident',
    INCIDENTS_API_URLS.INCIDENTS_CREATE_GENERIC_DEFECT
  ),

  postApartmentMaintenanceIncident: createAsyncTask(
    'post-maintenance-incident',
    (payload: DefaultIncidentRequest) =>
      authClient.post(
        INCIDENTS_API_URLS.INCIDENTS_CREATE_APARTMENT_MAINTENANCE,
        payload
      )
  ),

  postNewKeyRequest: createAsyncTask(
    'post-new-key-request',
    (data: OrderNewKeyRequest) =>
      authClient.post(INCIDENTS_API_URLS.INCIDENTS_CREATE_ORDER_KEY, data)
  ),

  postLostKeyRequest: createAsyncTask(
    'post-lost-key-incident',
    (data: DefaultIncidentRequest) =>
      authClient.post(INCIDENTS_API_URLS.INCIDENTS_CREATE_LOST_KEY, data)
  ),

  postNameUpdateRequest: createAsyncTask(
    'post-name-update-incident',
    (data: DefaultIncidentRequest) =>
      authClient.post(INCIDENTS_API_URLS.INCIDENTS_CREATE_NAME_UPDATE, data)
  ),
};
